// src/pages/SignupPage.tsx
import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { useSignUp } from "@clerk/clerk-react";
import AnimatedDotBackground from "../components/common/AnimatedDotBackground";
import BrandPanel from "../components/common/BrandPanel";
import { RED, RED_DARK, WHITE_BG, TEXT_BLACK, TEXT_MUTED } from "../utils/designTokens";

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { signUp, isLoaded: signUpLoaded, setActive } = useSignUp();

  // Form state (start empty instead of hardcoded)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");


  const validateForm = (): boolean => {
    if (!email.trim()) {
      setError("Email address is required");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (!password) {
      setError("Password is required");
      return false;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return false;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Check if Clerk is loaded
      if (!signUpLoaded || !signUp) {
        throw new Error("Authentication service is not ready. Please try again.");
      }

      // 1) Create the user with Clerk
      await signUp.create({
        emailAddress: email,
        password: password,
      });

      // 2) Send email verification code
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      
      // Show verification input
      setPendingVerification(true);
    } catch (err: any) {
      console.error("Signup error:", err);
      let msg = "Sign up failed. Please try again.";
      // Handle Clerk-specific errors
      if (err.errors && err.errors.length > 0) {
        const clerkError = err.errors[0];
        if (clerkError.code === "form_identifier_exists") {
          msg = "An account with this email already exists.";
        } else if (clerkError.code === "form_password_pwned") {
          msg = "This password has been compromised. Please choose a different password.";
        } else if (clerkError.code === "form_password_length_too_short") {
          msg = "Password should be at least 8 characters.";
        } else if (clerkError.message) {
          msg = clerkError.message;
        }
      } else if (err.message) {
        msg = err.message;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!signUp) {
        throw new Error("Authentication service is not ready.");
      }

      // Attempt to verify the email with the code
      const result = await signUp.attemptEmailAddressVerification({
        code: verificationCode,
      });

      if (result.status === "complete") {
        // Set the active session
        await setActive({ session: result.createdSessionId });
        
        // Redirect to login with success message
        navigate("/login", {
          state: {
            message: "Account created and verified. You can now sign in.",
          },
        });
      } else {
        setError("Verification incomplete. Please try again.");
      }
    } catch (err: any) {
      console.error("Verification error:", err);
      let msg = "Verification failed. Please try again.";
      if (err.errors && err.errors.length > 0) {
        const clerkError = err.errors[0];
        if (clerkError.code === "form_code_incorrect") {
          msg = "Incorrect verification code. Please try again.";
        } else if (clerkError.message) {
          msg = clerkError.message;
        }
      } else if (err.message) {
        msg = err.message;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: WHITE_BG,
        fontFamily:
          '"Plus Jakarta Sans", Inter, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
      }}
    >
      {/* WL Labs logo top-left */}
      <Box sx={{ position: "absolute", top: 18, left: 34.5, zIndex: 3 }}>
        <img
          src="/wl-labs-logo-white.svg"
          alt="WL Labs"
          style={{ height: 22, width: "auto" }}
        />
      </Box>

      {/* Animated Background */}
      <AnimatedDotBackground />

      {/* Centered two-panel card */}
      <Card
        elevation={0}
        sx={{
          position: "relative",
          zIndex: 2,
          width: "90%",
          maxWidth: 920,
          minHeight: 480,
          borderRadius: 4,
          display: "flex",
          overflow: "hidden",
          backgroundColor: "white",
          boxShadow:
            "0 26px 60px rgba(15,23,42,0.12), 0 10px 25px rgba(15,23,42,0.08)",
          border: "1px solid rgba(15,23,42,0.06)",
        }}
      >
        {/* LEFT Panel (brand section) */}
        <BrandPanel logoWidth={260} />

        {/* RIGHT Panel (signup form) */}
        <Box
          sx={{
            flex: 1,
            p: 6,
            pl: 4,
            background: "linear-gradient(180deg, #ffffff 0%, #fdfdfd 100%)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              textAlign: "center",
              fontSize: "1.5rem",
              fontWeight: 800,
              mb: 3,
              color: TEXT_BLACK,
            }}
          >
            {pendingVerification ? "Verify your email" : "Create your account"}
          </Typography>

          {!pendingVerification ? (
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                label="Email Address"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    { borderColor: RED },
                  "& .MuiFormLabel-root.Mui-focused": {
                    color: RED,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon sx={{ color: TEXT_MUTED }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    { borderColor: RED },
                  "& .MuiFormLabel-root.Mui-focused": {
                    color: RED,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: TEXT_MUTED }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    { borderColor: RED },
                  "& .MuiFormLabel-root.Mui-focused": {
                    color: RED,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: TEXT_MUTED }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <Typography sx={{ color: "error.main", mt: 1, mb: 1 }}>
                  {error}
                </Typography>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  py: 1.2,
                  background: RED,
                  "&:hover": { background: RED_DARK },
                  fontWeight: 800,
                  textTransform: "none",
                }}
              >
                {loading ? "Creating account..." : "Sign Up"}
              </Button>

              <Typography sx={{ textAlign: "center", mt: 2, color: TEXT_MUTED }}>
                Already have an account?{" "}
                <Link
                  sx={{ color: RED, fontWeight: 700, cursor: "pointer" }}
                  onClick={() => navigate("/login")}
                >
                  Sign in
                </Link>
              </Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleVerification}>
              <Typography sx={{ mb: 3, color: TEXT_MUTED, textAlign: "center" }}>
                We sent a verification code to {email}. Please enter it below.
              </Typography>

              <TextField
                label="Verification Code"
                type="text"
                fullWidth
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    { borderColor: RED },
                  "& .MuiFormLabel-root.Mui-focused": {
                    color: RED,
                  },
                }}
              />

              {error && (
                <Typography sx={{ color: "error.main", mt: 1, mb: 1 }}>
                  {error}
                </Typography>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  py: 1.2,
                  background: RED,
                  "&:hover": { background: RED_DARK },
                  fontWeight: 800,
                  textTransform: "none",
                }}
              >
                {loading ? "Verifying..." : "Verify Email"}
              </Button>

              <Typography sx={{ textAlign: "center", mt: 2, color: TEXT_MUTED }}>
                <Link
                  sx={{ color: RED, fontWeight: 700, cursor: "pointer" }}
                  onClick={() => {
                    setPendingVerification(false);
                    setVerificationCode("");
                    setError("");
                  }}
                >
                  Back to signup
                </Link>
              </Typography>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default SignupPage;
